/// <amd-module name="Repos/Discussion/Editor/Components/CollapsibleDiscussionHost" />
import * as React from "react";
import {
    IDiscussionThread,
    IDiscussionThreadCommonProps,
    IDiscussionThreadModel,
    IDiscussionThreadStore
} from "Repos/Discussion/Common/DiscussionThread.Contracts";
import * as Resources from "Repos/Discussion/Editor/Resources";
import { ScreenSize } from "VSS/Core/Util/Screen";
import { format } from "VSS/Core/Util/String";
import { IdentityRef } from "VSS/Platform/Contracts/WebApi";
import { Button } from "VSSUI/Button";
import { FocusZone, FocusZoneDirection } from "VSSUI/FocusZone";
import { IconSize } from "VSSUI/Icon";
import { Observer } from "VSSUI/Observer";
import { Panel } from "VSSUI/Panel";
import { Persona, PersonaSize } from "VSSUI/Persona";
import { css } from "VSSUI/Util";
import { ScreenSizeObserver } from "VSSUI/Utilities/ScreenSize";
import { ICollapsibleDiscussionHostProps } from "./CollapsibleDiscussionHost.Props";

function getDiscussionOwner(thread: IDiscussionThread, currentUser: IdentityRef): IdentityRef {
    const firstComment = thread.comments ? thread.comments[0] : undefined;
    return firstComment ? firstComment.author : currentUser;
}

function getExpandTooltipText(threadsToExpand: IDiscussionThread[], currentUser: IdentityRef): string {
    if (threadsToExpand.length > 1) {
        // Make sure we have unique thread owner names
        const threadOwners: { [name: string]: boolean } = {};
        threadsToExpand.forEach(t => (threadOwners[getDiscussionOwner(t, currentUser).displayName] = true));
        return format(Resources.ExpandThreadsTooltipText, threadsToExpand.length, Object.keys(threadOwners).join(", "));
    } else if (threadsToExpand.length > 0) {
        // Single thread collapsed
        const firstComment = threadsToExpand[0].comments ? threadsToExpand[0].comments[0] : undefined;
        return firstComment ? `${firstComment.author.displayName}: "${(firstComment.content || "").substr(0, 60)}..."` : "";
    }

    return "";
}

function getInvisibleThreads(threads: IDiscussionThreadModel[]): IDiscussionThreadModel[] {
    return threads.filter(t => !t.getVisible().value || !t.getExpanded().value);
}

/**
 * This component is rendered within the Monaco widget and hosts one or more
 * discussion threads who share the same line number to be displayed.
 *
 * It also tracks expand/collapse states of the threads to provide UI experience
 * around expand/collapse.
 */
export const CollapsibleDiscussionHost = (props: ICollapsibleDiscussionHostProps) => {
    const { className, currentUser, discussionStore, hasNoLineInfo, onRemoveThread, renderDiscussion, threads } = props;

    const containerRef = React.createRef<HTMLDivElement>();

    const [panelThreads, setPanelThreads] = React.useState<IDiscussionThreadModel[]>([]);
    const knownThreadIds = React.useRef<Set<number>>(new Set([]));
    const discussionPanelExpand = React.useRef<Button>(null);
    const discussionPanelCollapse = React.useRef<Button>(null);
    let isExpandFocused = React.useRef<boolean>(false);
    let isCollapseFocused = React.useRef<boolean>(false);
    function removeEmptyNewThreads() {
        for (const thread of threads.value) {
            if (thread.isNew() && !thread.isDirty().value) {
                onRemoveThread(thread.id);
            }
        }
    }

    React.useEffect(() => {
        props.onContentLoaded && props.onContentLoaded(containerRef.current);

        return removeEmptyNewThreads;
    }, []);

    const minDiscussionWidth = 450;

    // We observe "threads" for any thread updates coming from the server
    // and observe "filteredThreads" for any filter change performed on comments
    return (
        <div className={css(className, "repos-editor-discussion-host")} ref={containerRef}>
            <ScreenSizeObserver>
                {(screenSizeProps: { screenSize: ScreenSize }) => {
                    const usePanel = (screenSizeProps.screenSize === ScreenSize.xsmall || (props.diffWidth && props.diffWidth < minDiscussionWidth)) && !hasNoLineInfo;
                    return (
                        <Observer
                            threads={threads}
                            filteredThreads={discussionStore.getFilteredThreads()}
                            onUpdate={() => {
                                // Even when this component re-renders or when threads/filteredThreads change
                                // we only care here about the scenario where expand/collapse button was focused
                                // and we've switched "expanded", so we need to preserve the focus on the complementary button
                                if (isExpandFocused.current) {
                                    discussionPanelCollapse.current?.focus();
                                    isExpandFocused.current = false;
                                }
                                if (isCollapseFocused.current) {
                                    discussionPanelExpand.current?.focus();
                                    isCollapseFocused.current = false;
                                }
                            }}
                        >
                            {(observerProps: { threads: IDiscussionThreadModel[] }) => {
                                const invisibleThreads = usePanel ? observerProps.threads : getInvisibleThreads(observerProps.threads);
                                const expandTooltipText = getExpandTooltipText(
                                    invisibleThreads.map(t => t.thread),
                                    currentUser
                                );
                                const invisibleThreadCount = invisibleThreads.length;

                                if (usePanel) {
                                    // Check if any thread in the panel doesn't exist anymore (deleted or canceled without saving) so we have to update the panel.
                                    const remainderPanelThreads = panelThreads.filter(pt => observerProps.threads.some(ot => pt.id === ot.id));
                                    if (remainderPanelThreads.length !== panelThreads.length) {
                                        setPanelThreads(remainderPanelThreads);
                                    }

                                    // If a new thread was added and it's not coming from server (negative Id), pop up
                                    if (panelThreads.length === 0) {
                                        const newThreads = observerProps.threads.filter(t => !knownThreadIds.current.has(t.id));
                                        if (newThreads.length === 1 && newThreads[0].isNew() && !newThreads[0].isDirty().value) {
                                            setPanelThreads(newThreads);
                                        }
                                    }
                                }

                                knownThreadIds.current = new Set(observerProps.threads.map(t => t.id));

                                return (
                                    <>
                                        {invisibleThreadCount > 0 && !hasNoLineInfo ? (
                                            <Button
                                                ref={discussionPanelExpand}
                                                ariaLabel={expandTooltipText}
                                                className={css("repos-editor-discussion-expand")}
                                                onClick={() => {
                                                    if (usePanel) {
                                                        setPanelThreads([...observerProps.threads]);
                                                    } else {
                                                        discussionStore.applyFilter(invisibleThreads, true, true);
                                                    }
                                                }}
                                                tooltipProps={{ text: expandTooltipText }}
                                                onFocus={() => (isExpandFocused.current = true)}
                                                onBlur={() => (isExpandFocused.current = false)}
                                                tabIndex={0}
                                            >
                                                {invisibleThreadCount > 1 ? (
                                                    invisibleThreadCount
                                                ) : (
                                                    <Persona
                                                        identity={getDiscussionOwner(invisibleThreads[0].thread, currentUser)}
                                                        size={PersonaSize.size16}
                                                        tooltipProps={{ text: "" }}
                                                    />
                                                )}
                                            </Button>
                                        ) : null}
                                        {!usePanel && (
                                            <div className="flex-column rhythm-vertical-8">
                                                {sortThreads(observerProps.threads).map((thread, index) => {
                                                    return (
                                                        <DiscussionThreadHost
                                                            discussionStore={discussionStore}
                                                            hasNoLineInfo={hasNoLineInfo}
                                                            key={thread.id}
                                                            onRemoveThread={onRemoveThread}
                                                            renderDiscussion={renderDiscussion}
                                                            thread={thread}
                                                            expandButtonRef={index == 0 ? discussionPanelCollapse : undefined}
                                                            onFocus={() => (isCollapseFocused.current = true)}
                                                            onBlur={() => (isCollapseFocused.current = false)}
                                                        />
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </>
                                );
                            }}
                        </Observer>
                    );
                }}
            </ScreenSizeObserver>
            {panelThreads.length > 0 && (
                <DiscussionPanel
                    onDismiss={() => {
                        setPanelThreads([]);
                        removeEmptyNewThreads();
                    }}
                    onChangeStatus={thread => discussionStore.applyFilter([thread])}
                    onRemoveThread={onRemoveThread}
                    renderDiscussion={renderDiscussion}
                    threads={panelThreads}
                />
            )}
        </div>
    );
};

interface IDiscussionThreadHostProps {
    discussionStore: IDiscussionThreadStore;
    hasNoLineInfo: boolean;
    onRemoveThread: (threadId: number) => void;
    renderDiscussion: (props: IDiscussionThreadCommonProps) => JSX.Element;
    thread: IDiscussionThreadModel;
    expandButtonRef?: React.RefObject<Button>;
    onFocus: () => void;
    onBlur: () => void;
}

const DiscussionThreadHost = (props: IDiscussionThreadHostProps) => {
    const { discussionStore, hasNoLineInfo, onRemoveThread, renderDiscussion, thread } = props;

    const onChangeStatus = () => discussionStore.applyFilter([thread]);

    return thread.getVisible().value && thread.getExpanded().value ? (
        <Observer unread={thread.isUnread()}>
            {(unreadProps: { unread: boolean }) => (
                // Need to pass an empty object to focusGroupProps in order to start a new focus group zone which will not intefere with
                // the focus group for the line numbers in diff control.
                <FocusZone direction={FocusZoneDirection.Vertical} focusGroupProps={{}}>
                    <div className={css("repos-editor-discussion margin-vertical-8", unreadProps.unread && "repos-unread-thread-marker")}>
                        {!hasNoLineInfo && (
                            <Button
                                ref={props.expandButtonRef}
                                ariaLabel={Resources.CollapseThreadTooltipText}
                                className="repos-editor-discussion-collapse"
                                iconProps={{ iconName: "ChevronFold10", size: IconSize.small }}
                                onClick={() => discussionStore.applyFilter([thread], false, false)}
                                tooltipProps={{ text: Resources.CollapseThreadTooltipText }}
                                onFocus={props.onFocus}
                                onBlur={props.onBlur}
                            />
                        )}
                        {renderDiscussion({
                            className: "flex-grow",
                            onCancelThread: onRemoveThread,
                            onChangeStatus,
                            onDeleteThread: onRemoveThread,
                            thread
                        })}
                    </div>
                </FocusZone>
            )}
        </Observer>
    ) : null;
};

interface DiscussionPanelProps {
    onChangeStatus: (thread: IDiscussionThreadModel) => void;
    onDismiss: () => void;
    onRemoveThread: (threadId: number) => void;
    renderDiscussion: (props: IDiscussionThreadCommonProps) => JSX.Element;
    threads: IDiscussionThreadModel[];
}

const DiscussionPanel = (props: DiscussionPanelProps) => {
    const { onChangeStatus, onDismiss, onRemoveThread, renderDiscussion, threads } = props;
    const title = threads.length === 1 ? Resources.DiscussionPanelTitleOne : format(Resources.DiscussionPanelTitleMany, threads.length);

    return (
        <Panel onDismiss={onDismiss} titleProps={{ text: title }}>
            <div className="flex-column flex-grow repos-editor-discussion-host">
                {sortThreads(threads).map(thread => (
                    <Observer unread={thread.isUnread()} key={thread.id}>
                        {({ unread }: { unread: boolean }) => (
                            <div className={css("repos-editor-discussion margin-vertical-8", unread && "repos-unread-thread-marker")}>
                                {renderDiscussion({
                                    onCancelThread: onRemoveThread,
                                    onChangeStatus: () => onChangeStatus(thread),
                                    onDeleteThread: onRemoveThread,
                                    thread
                                })}
                            </div>
                        )}
                    </Observer>
                ))}
            </div>
        </Panel>
    );
};

function sortThreads(threads: IDiscussionThreadModel[]) {
    // Sort threads by id ascending if new, otherwise sort descending
    return threads.sort((t1, t2) => (t1.id > 0 && t2.id > 0 ? t2.id - t1.id : t1.id - t2.id));
}
